import { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts"

interface UserDashboardProps {
  setCurrentPage?: (page: "dashboard" | "timeEntry" | "timeEntry2" | "projects" | "users" | "weeklyReport" | "weeklyReportReview") => void
}

interface ProjectStats {
  projectName: string
  projectCategory: string
  totalHours: number
  percentage: number
  activityBreakdown: { activityCode: string; hours: number }[]
}

interface ActivityStats {
  activityCode: string
  totalHours: number
  percentage: number
}

// 활동 코드별 색상 (Recharts용)
function getActivityColor(activityCode: string): string {
  const colors: Record<string, string> = {
    "개발/설계": "#3B82F6",
    "영업/견적/PM/기획": "#10B981",
    "제작/조립/구매": "#EAB308",
    "시험/검증/테스트": "#EF4444",
    "설치/납품/AS": "#A855F7",
    "지원/문서/교육": "#EC4899",
    "미팅": "#6366F1",
    "이동": "#14B8A6"
  }
  return colors[activityCode] || "#6B7280"
}

export function UserDashboard({ setCurrentPage }: UserDashboardProps) {
  const [projectStats, setProjectStats] = useState<ProjectStats[]>([])
  const [activityStats, setActivityStats] = useState<ActivityStats[]>([])
  const [loading, setLoading] = useState(true)
  const [totalHours, setTotalHours] = useState(0)
  const [activityChartView, setActivityChartView] = useState<"bar" | "pie">("bar")
  const [hoveredActivity, setHoveredActivity] = useState<{ code: string; percentage: number } | null>(null)
  
  // 프로젝트별 그래프 선택 상태
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadStatistics()
  }, [])

  async function loadStatistics() {
    try {
      setLoading(true)
      
      const result = await lumi.entities.time_entries2.list({
        filter: {
          userId: lumi.auth.user?.userId,
          status: "submitted"
        }
      })

      if (!result.list || result.list.length === 0) {
        setLoading(false)
        return
      }

      // ... keep existing code
      const projectMap = new Map<string, { totalHours: number; activities: Map<string, number> }>()
      let total = 0

      result.list.forEach((entry: any) => {
        if (!entry.isVacation) {
          const projectName = entry.projectName
          
          if (!projectMap.has(projectName)) {
            projectMap.set(projectName, { totalHours: 0, activities: new Map(), category: entry.projectCategory })
          }
          
          const projectData = projectMap.get(projectName)!
          
          if (entry.dailyEntries) {
            entry.dailyEntries.forEach((daily: any) => {
              if (daily.activities) {
                daily.activities.forEach((activity: any) => {
                  const hours = activity.hours || 0
                  projectData.totalHours += hours
                  total += hours
                  
                  const currentActivityHours = projectData.activities.get(activity.activityCode) || 0
                  projectData.activities.set(activity.activityCode, currentActivityHours + hours)
                })
              }
            })
          }
        }
      })

      const projectData: ProjectStats[] = Array.from(projectMap.entries())
        .map(([name, data]) => ({
          projectName: name,
          projectCategory: (data as any).category || "",
          totalHours: data.totalHours,
          percentage: total > 0 ? (data.totalHours / total) * 100 : 0,
          activityBreakdown: Array.from(data.activities.entries())
            .map(([code, hours]) => ({ activityCode: code, hours }))
            .sort((a, b) => b.hours - a.hours)
        }))
        .sort((a, b) => b.totalHours - a.totalHours)

      const activityMap = new Map<string, number>()

      result.list.forEach((entry: any) => {
        if (!entry.isVacation && entry.dailyEntries) {
          entry.dailyEntries.forEach((daily: any) => {
            if (daily.activities) {
              daily.activities.forEach((activity: any) => {
                const current = activityMap.get(activity.activityCode) || 0
                activityMap.set(activity.activityCode, current + activity.hours)
              })
            }
          })
        }
      })

      const activityData: ActivityStats[] = Array.from(activityMap.entries())
        .map(([code, hours]) => ({
          activityCode: code,
          totalHours: hours,
          percentage: total > 0 ? (hours / total) * 100 : 0
        }))
        .sort((a, b) => b.totalHours - a.totalHours)

      setProjectStats(projectData)
      setActivityStats(activityData)
      setTotalHours(total)
      
      // 초기 로드 시 상위 10개 프로젝트 선택
      if (projectData.length > 0 && selectedProjects.size === 0) {
        const top10 = new Set(projectData.slice(0, 10).map((_, idx) => idx.toString()))
        setSelectedProjects(top10)
      }
    } catch (error) {
      console.error("통계 로드 실패:", error)
    } finally {
      setLoading(false)
    }
  }

  function getCategoryBadgeColor(category: string): string {
    switch (category) {
      case "납품장비":
        return "bg-blue-100 text-blue-700"
      case "제품개발":
        return "bg-green-100 text-green-700"
      case "연구개발":
        return "bg-purple-100 text-purple-700"
      case "경영지원":
        return "bg-gray-100 text-gray-700"
      case "연차":
      case "오전반차":
      case "오후반차":
        return "bg-pink-100 text-pink-700"
      default:
        return "bg-gray-100 text-gray-600"
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm sm:text-base">통계를 불러오는 중...</p>
        </div>
      </div>
    )
  }

  if (totalHours === 0) {
    return (
      <div className="p-4 sm:p-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-12 text-center">
          <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-gray-600 text-sm sm:text-base">아직 제출된 시간 입력 데이터가 없습니다.</p>
          <p className="text-xs sm:text-sm text-gray-500 mt-2">시간을 입력하고 제출하면 여기에 통계가 표시됩니다.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">나의 활동 통계</h2>
        <p className="text-sm sm:text-base text-gray-600">제출된 투입시간 데이터를 기반으로 통계를 확인하세요</p>
      </div>

      {/* 투입시간 입력 방식 선택 */}
      {setCurrentPage && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <button 
            onClick={() => setCurrentPage('timeEntry')}
            className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 text-left group"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">기존</span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">프로젝트별 입력 방식</h3>
            <p className="text-sm text-gray-600 mb-3">월~금 5일, 프로젝트별 활동 시간 입력</p>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>주간 최대 40시간 (연차 제외)</span>
            </div>
          </button>
          
          {/* 배포 전 임시 비활성화 - 야근 관리 방식 */}
          {/* <button 
            onClick={() => setCurrentPage('timeEntry2')}
            className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6 hover:from-orange-100 hover:to-amber-100 transition-all duration-200 text-left group"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span className="px-3 py-1 bg-orange-600 text-white text-xs font-bold rounded-full">NEW</span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">야근 관리 방식 (테스트)</h3>
            <p className="text-sm text-gray-600 mb-3">월~일 7일, 정규/야근 시간 분리 관리</p>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>정규 40시간 + 야근 별도 집계</span>
            </div>
          </button> */}
        </div>
      )}

      {/* 총 시간 카드 */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">총 투입 시간 (연차/반차 제외)</p>
            <p className="text-2xl sm:text-4xl font-bold text-gray-900">{totalHours.toFixed(1)}<span className="text-lg sm:text-2xl text-gray-600 ml-1 sm:ml-2">시간</span></p>
          </div>
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* 프로젝트별 누적 막대 그래프 (Stacked Bar Chart) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <span className="text-sm sm:text-base">프로젝트별 투입시간 (활동코드별 누적)</span>
          </h3>
          
          {projectStats.length === 0 ? (
            <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-sm">데이터가 없습니다</p>
            </div>
          ) : (
            <>
              {/* 프로젝트 선택 체크박스 */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-700">표시할 프로젝트:</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        const allProjects = new Set(projectStats.map((_, idx) => idx.toString()))
                        setSelectedProjects(allProjects)
                      }}
                      className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                    >
                      전체선택
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => {
                        setSelectedProjects(new Set())
                      }}
                      className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                    >
                      전체해제
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => {
                        const top10 = new Set(projectStats.slice(0, 10).map((_, idx) => idx.toString()))
                        setSelectedProjects(top10)
                      }}
                      className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                    >
                      상위 10개
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                  {projectStats.map((project, idx) => (
                    <label key={idx} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-100 p-1 rounded transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedProjects.has(idx.toString())}
                        onChange={(e) => {
                          const newSelected = new Set(selectedProjects)
                          if (e.target.checked) {
                            newSelected.add(idx.toString())
                          } else {
                            newSelected.delete(idx.toString())
                          }
                          setSelectedProjects(newSelected)
                        }}
                        className="w-3 h-3 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <span className="text-gray-700 truncate" title={project.projectName}>
                        {project.projectName}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 그래프 */}
              <div className="h-[400px] sm:h-[500px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={projectStats
                      .filter((_, idx) => selectedProjects.has(idx.toString()))
                      .map(p => ({
                        name: p.projectName.length > 12 ? p.projectName.substring(0, 12) + '...' : p.projectName,
                        fullName: p.projectName,
                        ...p.activityBreakdown.reduce((acc, a) => ({ ...acc, [a.activityCode]: a.hours }), {})
                      }))}
                    margin={{ top: 20, right: 10, left: 0, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={80}
                      style={{ fontSize: '10px' }}
                    />
                    <YAxis style={{ fontSize: '10px' }} />
                    <Tooltip 
                      labelFormatter={(label, payload) => {
                        if (payload && payload[0]) {
                          return payload[0].payload.fullName
                        }
                        return label
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '9px' }} />
                    <Bar dataKey="개발/설계" stackId="a" fill="#3B82F6" name="개발/설계" />
                    <Bar dataKey="영업/견적/PM/기획" stackId="a" fill="#10B981" name="영업/견적/PM/기획" />
                    <Bar dataKey="제작/조립/구매" stackId="a" fill="#EAB308" name="제작/조립/구매" />
                    <Bar dataKey="시험/검증/테스트" stackId="a" fill="#EF4444" name="시험/검증/테스트" />
                    <Bar dataKey="설치/납품/AS" stackId="a" fill="#A855F7" name="설치/납품/AS" />
                    <Bar dataKey="지원/문서/교육" stackId="a" fill="#EC4899" name="지원/문서/교육" />
                    <Bar dataKey="미팅" stackId="a" fill="#6366F1" name="미팅" />
                    <Bar dataKey="이동" stackId="a" fill="#14B8A6" name="이동" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>

        {/* 활동 코드별 통계 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
              </svg>
              <span className="text-sm sm:text-base">활동 코드별 투입시간</span>
            </h3>
            <div className="flex gap-1 sm:gap-2">
              <button
                onClick={() => setActivityChartView("bar")}
                className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  activityChartView === "bar"
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                막대
              </button>
              <button
                onClick={() => setActivityChartView("pie")}
                className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  activityChartView === "pie"
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                파이
              </button>
            </div>
          </div>
          
          {activityChartView === "bar" ? (
            <div className="h-[400px] sm:h-[500px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={activityStats} margin={{ top: 20, right: 10, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="activityCode" 
                    angle={-45} 
                    textAnchor="end" 
                    height={80}
                    style={{ fontSize: '10px' }}
                  />
                  <YAxis style={{ fontSize: '10px' }} />
                  <Tooltip />
                  <Bar dataKey="totalHours" name="투입시간">
                    {activityStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getActivityColor(entry.activityCode)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="relative w-48 h-48 sm:w-64 sm:h-64 mb-4">
                <svg viewBox="0 0 200 200" className="transform -rotate-90">
                  {activityStats.map((activity, index) => {
                    const startAngle = activityStats
                      .slice(0, index)
                      .reduce((sum, a) => sum + (a.percentage / 100) * 360, 0)
                    const angle = (activity.percentage / 100) * 360
                    const endAngle = startAngle + angle
                    
                    const startX = 100 + 80 * Math.cos((startAngle * Math.PI) / 180)
                    const startY = 100 + 80 * Math.sin((startAngle * Math.PI) / 180)
                    const endX = 100 + 80 * Math.cos((endAngle * Math.PI) / 180)
                    const endY = 100 + 80 * Math.sin((endAngle * Math.PI) / 180)
                    
                    const largeArcFlag = angle > 180 ? 1 : 0
                    
                    return (
                      <path
                        key={index}
                        d={`M 100 100 L ${startX} ${startY} A 80 80 0 ${largeArcFlag} 1 ${endX} ${endY} Z`}
                        fill={getActivityColor(activity.activityCode)}
                        className="transition-all hover:opacity-80 cursor-pointer"
                        onMouseEnter={() => setHoveredActivity({ code: activity.activityCode, percentage: activity.percentage })}
                        onMouseLeave={() => setHoveredActivity(null)}
                      />
                    )
                  })}
                </svg>
                
                {/* Tooltip Modal */}
                {hoveredActivity && (
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-xl pointer-events-none z-10">
                    <p className="text-sm font-semibold whitespace-nowrap">{hoveredActivity.code}</p>
                    <p className="text-xs text-gray-300">{hoveredActivity.percentage.toFixed(1)}%</p>
                  </div>
                )}
              </div>
              <div className="space-y-2 w-full">
                {activityStats.map((activity, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div
                        className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0"
                        style={{ backgroundColor: getActivityColor(activity.activityCode) }}
                      ></div>
                      <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">{activity.activityCode}</span>
                    </div>
                    <span className="text-xs sm:text-sm text-gray-600 whitespace-nowrap ml-2">{activity.totalHours.toFixed(1)}h ({activity.percentage.toFixed(1)}%)</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 활동 코드 색상 범례 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mt-4 sm:mt-6">
        <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">활동 코드 색상 범례</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
          {[
            { code: "개발/설계", color: "#3B82F6" },
            { code: "영업/견적/PM/기획", color: "#10B981" },
            { code: "제작/조립/구매", color: "#EAB308" },
            { code: "시험/검증/테스트", color: "#EF4444" },
            { code: "설치/납품/AS", color: "#A855F7" },
            { code: "지원/문서/교육", color: "#EC4899" },
            { code: "미팅", color: "#6366F1" },
            { code: "이동", color: "#14B8A6" }
          ].map(activity => (
            <div key={activity.code} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0"
                style={{ backgroundColor: activity.color }}
              ></div>
              <span className="text-xs sm:text-sm text-gray-700">{activity.code}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}